<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { flowbuilderAPI } from 'dashboard/api/flowbuilder';

import DashboardAppsAPI from 'dashboard/api/dashboardApps';
import InboxDashboardAppAutomationsAPI from 'dashboard/api/inboxDashboardAppAutomations';
import InboxContactDataSourceAPI from 'dashboard/api/inboxContactDataSource';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { flowReference, isDataSourceReference } from './dataSourceReference';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const dashboardApps = ref([]);
const publishedFlows = ref([]);
const configurations = reactive({});
const saving = reactive({});
const submitted = reactive({});
const expandedDashboardApps = reactive({});
const isLoading = ref(false);
const isContactDataSourceExpanded = ref(false);
const contactDataSource = reactive({
  enabled: false,
  data_url: '',
  refresh_ttl_seconds: 300,
  refresh_on_message: true,
});
const isSavingContactDataSource = ref(false);
const contactDataSourceSubmitted = ref(false);
const contactContractExample = JSON.stringify({
  version: '1',
  result: 'success',
  data: {
    contact_attributes: {
      stripe_customer_id: 'cus_...',
      bubble_user_id: 'user_...',
    },
  },
});
const jsonContractExample = JSON.stringify({
  version: '1',
  result: 'success',
  data: {},
});

const isDashboardAppExpanded = dashboardAppId =>
  Boolean(expandedDashboardApps[dashboardAppId]);

const toggleDashboardApp = dashboardAppId => {
  expandedDashboardApps[dashboardAppId] =
    !expandedDashboardApps[dashboardAppId];
};

const collapseContactDataSourceWhenDisabled = enabled => {
  if (!enabled) isContactDataSourceExpanded.value = false;
};

const collapseDashboardAppWhenDisabled = (dashboardAppId, enabled) => {
  if (!enabled) expandedDashboardApps[dashboardAppId] = false;
};

const contactDataSourceToggleLabel = computed(() => {
  const title = t('INBOX_MGMT.DATA_SOURCES.CONTACT.TITLE');
  return isContactDataSourceExpanded.value
    ? t('INBOX_MGMT.DATA_SOURCES.COLLAPSE_SECTION', { title })
    : t('INBOX_MGMT.DATA_SOURCES.EXPAND_SECTION', { title });
});

const dashboardAppToggleLabel = dashboardApp =>
  isDashboardAppExpanded(dashboardApp.id)
    ? t('INBOX_MGMT.DATA_SOURCES.COLLAPSE_SECTION', {
        title: dashboardApp.title,
      })
    : t('INBOX_MGMT.DATA_SOURCES.EXPAND_SECTION', {
        title: dashboardApp.title,
      });

const defaultConfiguration = dashboardAppId => ({
  dashboard_app_id: dashboardAppId,
  enabled: false,
  data_url: '',
  refresh_ttl_seconds: 300,
  refresh_on_message: true,
  refresh_on_dashboard_open: true,
  allow_cortex_access: false,
  description: '',
});

const normalizeConfiguration = (dashboardAppId, configuration = {}) => ({
  ...defaultConfiguration(dashboardAppId),
  ...configuration,
  data_url: configuration.data_url || '',
  description: configuration.description || '',
});

const flowOptions = computed(() =>
  publishedFlows.value.map(flow => ({
    value: flowReference(flow.id),
    label: flow.name,
  }))
);

const endpointError = dashboardAppId => {
  const configuration = configurations[dashboardAppId];
  if (!submitted[dashboardAppId] || !configuration?.enabled) return '';
  return isDataSourceReference(configuration.data_url)
    ? ''
    : t('INBOX_MGMT.DATA_SOURCES.ENDPOINT_ERROR');
};

const intervalError = dashboardAppId => {
  const value = Number(configurations[dashboardAppId]?.refresh_ttl_seconds);
  if (!submitted[dashboardAppId]) return '';
  return value >= 60 && value <= 86400
    ? ''
    : t('INBOX_MGMT.DATA_SOURCES.INTERVAL_ERROR');
};

const isValid = dashboardAppId =>
  !endpointError(dashboardAppId) && !intervalError(dashboardAppId);

const contactEndpointError = () => {
  if (!contactDataSourceSubmitted.value || !contactDataSource.enabled)
    return '';
  return isDataSourceReference(contactDataSource.data_url)
    ? ''
    : t('INBOX_MGMT.DATA_SOURCES.CONTACT.ENDPOINT_ERROR');
};

const contactIntervalError = () => {
  if (!contactDataSourceSubmitted.value) return '';
  const value = Number(contactDataSource.refresh_ttl_seconds);
  return value >= 60 && value <= 86400
    ? ''
    : t('INBOX_MGMT.DATA_SOURCES.CONTACT.INTERVAL_ERROR');
};

const loadConfigurations = async () => {
  if (!props.inbox.id) return;

  isLoading.value = true;
  dashboardApps.value = [];
  isContactDataSourceExpanded.value = false;
  Object.keys(configurations).forEach(key => delete configurations[key]);
  Object.keys(submitted).forEach(key => delete submitted[key]);
  Object.keys(expandedDashboardApps).forEach(
    key => delete expandedDashboardApps[key]
  );
  try {
    const [appsResponse, configurationsResponse, contactResponse] =
      await Promise.all([
        DashboardAppsAPI.get(),
        InboxDashboardAppAutomationsAPI.get(props.inbox.id),
        InboxContactDataSourceAPI.get(props.inbox.id),
      ]);
    Object.assign(contactDataSource, {
      enabled: false,
      refresh_ttl_seconds: 300,
      refresh_on_message: true,
      ...contactResponse.data,
      data_url: contactResponse.data.data_url || '',
    });
    dashboardApps.value = appsResponse.data;
    const existingByApp = Object.fromEntries(
      configurationsResponse.data.map(configuration => [
        configuration.dashboard_app_id,
        configuration,
      ])
    );
    dashboardApps.value.forEach(dashboardApp => {
      configurations[dashboardApp.id] = normalizeConfiguration(
        dashboardApp.id,
        existingByApp[dashboardApp.id]
      );
    });
    try {
      const { data } = await flowbuilderAPI.list({
        status: 'published',
        execution_profile: 'synchronous_http',
        per_page: 100,
      });
      publishedFlows.value = data.payload || [];
    } catch {
      publishedFlows.value = [];
    }
  } catch {
    useAlert(t('INBOX_MGMT.DATA_SOURCES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const saveContactDataSource = async () => {
  contactDataSourceSubmitted.value = true;
  if (contactEndpointError() || contactIntervalError()) {
    isContactDataSourceExpanded.value = true;
    return;
  }

  isSavingContactDataSource.value = true;
  try {
    const response = await InboxContactDataSourceAPI.update(props.inbox.id, {
      ...contactDataSource,
      data_url: contactDataSource.data_url.trim(),
      refresh_ttl_seconds: Number(contactDataSource.refresh_ttl_seconds),
    });
    Object.assign(contactDataSource, response.data, {
      data_url: response.data.data_url || '',
    });
    contactDataSourceSubmitted.value = false;
    useAlert(t('INBOX_MGMT.DATA_SOURCES.CONTACT.SAVE_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.DATA_SOURCES.CONTACT.SAVE_ERROR'));
  } finally {
    isSavingContactDataSource.value = false;
  }
};

const saveConfiguration = async dashboardAppId => {
  submitted[dashboardAppId] = true;
  if (!isValid(dashboardAppId)) {
    expandedDashboardApps[dashboardAppId] = true;
    return;
  }

  const configuration = configurations[dashboardAppId];
  saving[dashboardAppId] = true;
  try {
    const response = await InboxDashboardAppAutomationsAPI.update(
      props.inbox.id,
      dashboardAppId,
      {
        ...configuration,
        data_url: configuration.data_url.trim(),
        description: configuration.description.trim(),
        refresh_ttl_seconds: Number(configuration.refresh_ttl_seconds),
      }
    );
    configurations[dashboardAppId] = normalizeConfiguration(
      dashboardAppId,
      response.data
    );
    submitted[dashboardAppId] = false;
    useAlert(t('INBOX_MGMT.DATA_SOURCES.SAVE_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.DATA_SOURCES.SAVE_ERROR'));
  } finally {
    saving[dashboardAppId] = false;
  }
};

watch(() => props.inbox.id, loadConfigurations);
onMounted(loadConfigurations);
</script>

<template>
  <div class="flex max-w-5xl flex-col gap-5">
    <div class="flex flex-col gap-1">
      <h2 class="m-0 text-heading-2 text-n-slate-12">
        {{ $t('INBOX_MGMT.DATA_SOURCES.TITLE') }}
      </h2>
      <p class="m-0 text-label-small leading-4 text-n-slate-11">
        {{ $t('INBOX_MGMT.DATA_SOURCES.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex min-h-40 items-center justify-center">
      <Spinner />
    </div>

    <template v-else>
      <form
        class="flex flex-col gap-4 rounded-lg border border-n-weak p-4"
        @submit.prevent="saveContactDataSource"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <h3 class="m-0 text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.DATA_SOURCES.CONTACT.TITLE') }}
            </h3>
            <p class="m-0 mt-1 text-label-small leading-4 text-n-slate-11">
              {{ $t('INBOX_MGMT.DATA_SOURCES.CONTACT.DESCRIPTION') }}
            </p>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <NextButton
              type="button"
              sm
              slate
              ghost
              :icon="
                isContactDataSourceExpanded
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
              :disabled="!contactDataSource.enabled"
              :aria-expanded="isContactDataSourceExpanded"
              aria-controls="contact-data-source-fields"
              :aria-label="contactDataSourceToggleLabel"
              :title="contactDataSourceToggleLabel"
              @click="
                isContactDataSourceExpanded = !isContactDataSourceExpanded
              "
            />
            <ToggleSwitch
              v-model="contactDataSource.enabled"
              @change="collapseContactDataSourceWhenDisabled"
            />
          </div>
        </div>

        <div
          v-if="contactDataSource.enabled && isContactDataSourceExpanded"
          id="contact-data-source-fields"
          class="flex flex-col gap-4"
        >
          <AdaptiveSelect
            v-if="flowOptions.length"
            v-model="contactDataSource.data_url"
            :options="flowOptions"
            searchable
            :display-label="$t('INBOX_MGMT.DATA_SOURCES.FLOW_REFERENCE_LABEL')"
            :placeholder="
              $t('INBOX_MGMT.DATA_SOURCES.FLOW_REFERENCE_PLACEHOLDER')
            "
          />
          <NextInput
            v-model="contactDataSource.data_url"
            :label="$t('INBOX_MGMT.DATA_SOURCES.CONTACT.ENDPOINT_LABEL')"
            :placeholder="
              $t('INBOX_MGMT.DATA_SOURCES.CONTACT.ENDPOINT_PLACEHOLDER')
            "
            :message="
              contactEndpointError() ||
              $t('INBOX_MGMT.DATA_SOURCES.CONTACT.ENDPOINT_HINT')
            "
            :message-type="contactEndpointError() ? 'error' : 'info'"
          />

          <NextInput
            v-model="contactDataSource.refresh_ttl_seconds"
            type="number"
            min="60"
            max="86400"
            :label="$t('INBOX_MGMT.DATA_SOURCES.CONTACT.INTERVAL_LABEL')"
            :message="
              contactIntervalError() ||
              $t('INBOX_MGMT.DATA_SOURCES.CONTACT.INTERVAL_HINT')
            "
            :message-type="contactIntervalError() ? 'error' : 'info'"
          />

          <label
            class="flex items-center justify-between gap-4 border-t border-n-weak pt-4"
          >
            <span class="text-body-main text-n-slate-12">
              {{ $t('INBOX_MGMT.DATA_SOURCES.CONTACT.REFRESH_ON_MESSAGE') }}
            </span>
            <ToggleSwitch v-model="contactDataSource.refresh_on_message" />
          </label>

          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="m-0 text-label-small leading-4 text-n-slate-11">
              {{ $t('INBOX_MGMT.DATA_SOURCES.CONTACT.CONSENT_HINT') }}
            </p>
            <code class="mt-2 block break-all text-xs text-n-slate-12">
              {{ contactContractExample }}
            </code>
          </div>
        </div>

        <div class="flex justify-end border-t border-n-weak pt-4">
          <NextButton
            type="submit"
            icon="i-lucide-save"
            :label="$t('INBOX_MGMT.DATA_SOURCES.CONTACT.SAVE')"
            :is-loading="isSavingContactDataSource"
          />
        </div>
      </form>

      <div class="flex flex-col gap-1 border-t border-n-weak pt-5">
        <h3 class="m-0 text-heading-3 text-n-slate-12">
          {{ $t('INBOX_MGMT.DATA_SOURCES.DASHBOARD_TITLE') }}
        </h3>
        <p class="m-0 text-label-small leading-4 text-n-slate-11">
          {{ $t('INBOX_MGMT.DATA_SOURCES.DASHBOARD_DESCRIPTION') }}
        </p>
      </div>

      <div
        v-if="!dashboardApps.length"
        class="rounded-lg border border-n-weak p-5 text-body-small text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.DATA_SOURCES.EMPTY') }}
      </div>

      <form
        v-for="dashboardApp in dashboardApps"
        :key="dashboardApp.id"
        class="flex flex-col gap-4 rounded-lg border border-n-weak p-4"
        @submit.prevent="saveConfiguration(dashboardApp.id)"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <h3 class="m-0 text-heading-3 text-n-slate-12">
              {{ dashboardApp.title }}
            </h3>
            <p
              class="m-0 mt-1 break-all text-label-small leading-4 text-n-slate-11"
            >
              {{ dashboardApp.content?.[0]?.url }}
            </p>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <NextButton
              type="button"
              sm
              slate
              ghost
              :icon="
                isDashboardAppExpanded(dashboardApp.id)
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
              :disabled="!configurations[dashboardApp.id].enabled"
              :aria-expanded="isDashboardAppExpanded(dashboardApp.id)"
              :aria-controls="`dashboard-data-source-${dashboardApp.id}`"
              :aria-label="dashboardAppToggleLabel(dashboardApp)"
              :title="dashboardAppToggleLabel(dashboardApp)"
              @click="toggleDashboardApp(dashboardApp.id)"
            />
            <ToggleSwitch
              v-model="configurations[dashboardApp.id].enabled"
              @change="
                enabled =>
                  collapseDashboardAppWhenDisabled(dashboardApp.id, enabled)
              "
            />
          </div>
        </div>

        <div
          v-if="
            configurations[dashboardApp.id].enabled &&
            isDashboardAppExpanded(dashboardApp.id)
          "
          :id="`dashboard-data-source-${dashboardApp.id}`"
          class="flex flex-col gap-4"
        >
          <AdaptiveSelect
            v-if="flowOptions.length"
            v-model="configurations[dashboardApp.id].data_url"
            :options="flowOptions"
            searchable
            :display-label="$t('INBOX_MGMT.DATA_SOURCES.FLOW_REFERENCE_LABEL')"
            :placeholder="
              $t('INBOX_MGMT.DATA_SOURCES.FLOW_REFERENCE_PLACEHOLDER')
            "
          />
          <NextInput
            v-model="configurations[dashboardApp.id].data_url"
            :label="$t('INBOX_MGMT.DATA_SOURCES.ENDPOINT_LABEL')"
            :placeholder="$t('INBOX_MGMT.DATA_SOURCES.ENDPOINT_PLACEHOLDER')"
            :message="
              endpointError(dashboardApp.id) ||
              $t('INBOX_MGMT.DATA_SOURCES.ENDPOINT_HINT')
            "
            :message-type="endpointError(dashboardApp.id) ? 'error' : 'info'"
          />

          <NextInput
            v-model="configurations[dashboardApp.id].refresh_ttl_seconds"
            type="number"
            min="60"
            max="86400"
            :label="$t('INBOX_MGMT.DATA_SOURCES.INTERVAL_LABEL')"
            :message="
              intervalError(dashboardApp.id) ||
              $t('INBOX_MGMT.DATA_SOURCES.INTERVAL_HINT')
            "
            :message-type="intervalError(dashboardApp.id) ? 'error' : 'info'"
          />

          <TextArea
            v-model="configurations[dashboardApp.id].description"
            :label="$t('INBOX_MGMT.DATA_SOURCES.CORTEX_DESCRIPTION_LABEL')"
            :placeholder="
              $t('INBOX_MGMT.DATA_SOURCES.CORTEX_DESCRIPTION_PLACEHOLDER')
            "
            :max-length="2000"
            show-character-count
          />

          <div class="flex flex-col gap-3 border-t border-n-weak pt-4">
            <label class="flex items-center justify-between gap-4">
              <span class="text-body-main text-n-slate-12">
                {{ $t('INBOX_MGMT.DATA_SOURCES.REFRESH_ON_MESSAGE') }}
              </span>
              <ToggleSwitch
                v-model="configurations[dashboardApp.id].refresh_on_message"
              />
            </label>
            <label class="flex items-center justify-between gap-4">
              <span class="text-body-main text-n-slate-12">
                {{ $t('INBOX_MGMT.DATA_SOURCES.REFRESH_ON_OPEN') }}
              </span>
              <ToggleSwitch
                v-model="
                  configurations[dashboardApp.id].refresh_on_dashboard_open
                "
              />
            </label>
            <label class="flex items-center justify-between gap-4">
              <span class="text-body-main text-n-slate-12">
                {{ $t('INBOX_MGMT.DATA_SOURCES.ALLOW_CORTEX') }}
              </span>
              <ToggleSwitch
                v-model="configurations[dashboardApp.id].allow_cortex_access"
              />
            </label>
          </div>

          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="m-0 text-label-small leading-4 text-n-slate-11">
              {{ $t('INBOX_MGMT.DATA_SOURCES.CONTRACT_HINT') }}
            </p>
            <code class="mt-2 block break-all text-xs text-n-slate-12">
              {{ jsonContractExample }}
            </code>
          </div>
        </div>

        <div class="flex justify-end border-t border-n-weak pt-4">
          <NextButton
            type="submit"
            icon="i-lucide-save"
            :label="$t('INBOX_MGMT.DATA_SOURCES.SAVE')"
            :is-loading="saving[dashboardApp.id]"
          />
        </div>
      </form>
    </template>
  </div>
</template>
